/**
 * ONE shared Web Audio context for the whole platform.
 *
 * Why this exists (mobile audio root cause):
 *  - iOS Safari hard-limits how many AudioContexts a page may hold (~4).
 *    Screens that created a fresh context per sound/per effect silently
 *    stopped producing audio once that limit was reached — no error, no
 *    console warning, just silence.
 *  - A context created outside a user gesture (in an effect, a setTimeout,
 *    a staggered mix) starts `suspended` on iOS and Android Chrome. Nothing
 *    ever resumed it, so scheduled nodes played into a frozen clock.
 *
 * Rules for callers:
 *  - Always obtain the context through `getAudioContext()`.
 *  - Call `unlockAudio()` synchronously inside the tap/click handler that
 *    starts playback, then build your graph.
 *  - NEVER call `ctx.close()` — the context is shared. Stop and disconnect
 *    your own nodes instead.
 */

let sharedCtx: AudioContext | null = null;
let unlocked = false;
let interruptionGuardInstalled = false;

type WindowWithWebkit = Window & { webkitAudioContext?: typeof AudioContext };

/**
 * iOS suspends (and reports "interrupted" on) the AudioContext when the phone
 * is locked, a call arrives, or the tab is backgrounded — and does NOT resume
 * it on return. Without this, a member who locks their phone mid-meditation
 * comes back to a player that looks like it is running but is silent.
 *
 * Resuming a context that was already unlocked by a real tap is permitted
 * outside a gesture, so this is a safe best-effort recovery.
 */
function installInterruptionGuard(): void {
  if (interruptionGuardInstalled || typeof document === "undefined") return;
  interruptionGuardInstalled = true;
  const recover = () => {
    if (!unlocked) return; // never force audio the member never started
    const ctx = sharedCtx;
    if (ctx && ctx.state !== "running") void ctx.resume().catch(() => {});
  };
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") recover();
  });
  window.addEventListener("pageshow", recover);
  window.addEventListener("focus", recover);
}

/** Returns the single shared AudioContext, creating it on first use. */
export function getAudioContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (!sharedCtx) {
    const Ctor = window.AudioContext || (window as WindowWithWebkit).webkitAudioContext;
    if (!Ctor) return null;
    try {
      sharedCtx = new Ctor();
    } catch {
      return null;
    }
    installInterruptionGuard();
  }
  return sharedCtx;
}


/** True once the shared context is actually running (audible). */
export function isAudioUnlocked(): boolean {
  return unlocked && sharedCtx?.state === "running";
}

/**
 * iPhone root cause for "Play shows but there's no sound":
 * Web Audio on iOS runs in the "ambient" audio category, which the ring/silent
 * switch mutes completely. Generated sounds (sound baths, EFT background,
 * soundscapes) were therefore silent for anyone with the phone on silent.
 * Fix: declare a "playback" session (Safari 16.4+) and, for older iOS and the
 * app's web view, start a looping silent <audio> element inside the tap —
 * an active media element promotes the whole page to the playback category.
 * MUST run synchronously inside the user's tap.
 */
let sessionKeeper: HTMLAudioElement | null = null;
const SILENT_WAV =
  "data:audio/wav;base64,UklGRrQBAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YZABAACAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICA";
export function armPlaybackSession(): void {
  if (typeof window === "undefined") return;
  try {
    const nav = navigator as Navigator & { audioSession?: { type: string } };
    if (nav.audioSession && nav.audioSession.type !== "playback") nav.audioSession.type = "playback";
  } catch {
    /* best effort */
  }
  try {
    if (!sessionKeeper) {
      const a = new Audio(SILENT_WAV);
      a.dataset.audioBusIgnore = "true";
      a.loop = true;
      a.setAttribute("playsinline", "");
      sessionKeeper = a;
    }
    if (sessionKeeper.paused) void sessionKeeper.play().catch(() => {});
  } catch {
    /* best effort */
  }
}

/**
 * Undo armPlaybackSession() before microphone capture. On iOS an audio
 * session left in the "playback" category makes getUserMedia fail with a
 * generic error — the exact "couldn't reach your microphone" report. Voice
 * recording calls this first, then re-arms playback only if it plays sound.
 */
export function releasePlaybackSession(): void {
  if (typeof window === "undefined") return;
  try {
    if (sessionKeeper && !sessionKeeper.paused) sessionKeeper.pause();
  } catch {
    /* best effort */
  }
  try {
    const nav = navigator as Navigator & { audioSession?: { type: string } };
    if (nav.audioSession && nav.audioSession.type !== "auto") nav.audioSession.type = "auto";
  } catch {
    /* best effort */
  }
}

/**
 * Resume the shared context. MUST be called synchronously from a user
 * gesture handler the first time; afterwards it is a cheap no-op.
 * Resolves to `true` when the context is running and audio will be heard.
 */
export async function unlockAudio(): Promise<boolean> {
  armPlaybackSession();
  const ctx = getAudioContext();
  if (!ctx) return false;
  if (ctx.state !== "running") {
    try {
      await ctx.resume();
    } catch {
      /* resume rejects when called outside a gesture — caller reports it */
    }
  }
  if (ctx.state === "running" && !unlocked) {
    // A zero-length silent buffer nudges iOS into fully arming the output.
    try {
      const src = ctx.createBufferSource();
      src.buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
      src.connect(ctx.destination);
      src.start(0);
    } catch {
      /* non-fatal */
    }
  }
  unlocked = ctx.state === "running";
  return unlocked;
}

/**
 * Best-effort resume used by non-gesture code paths (timers, staggered
 * mixes). Never throws.
 */
export function resumeAudio(): void {
  const ctx = sharedCtx;
  if (ctx && ctx.state !== "running") void ctx.resume().catch(() => {});
}

/**
 * Human-readable reason playback could not start, for user-facing errors.
 * Returns null when everything is fine.
 */
export function audioFailureReason(): string | null {
  if (typeof window === "undefined") return null;
  const Ctor = window.AudioContext || (window as WindowWithWebkit).webkitAudioContext;
  if (!Ctor) return "This browser does not support the sound engine. Please try Safari or Chrome.";
  const ctx = getAudioContext();
  if (!ctx) return "Sound could not be started on this device.";
  if (ctx.state !== "running") {
    return "Sound is blocked until you tap play. If your phone is on silent, switch the ring/silent slider on.";
  }
  return null;
}
